import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react'
import useBus from 'use-bus'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CompanyContext } from './CompanyContext'
import { useSocialNetworks } from 'utils/hooks/socials/useSocialNetworks'
import { BULK_PROGRESS_READY_EVENT } from './BulkProgressContext'
import { api } from 'api/http'

/**
 * Aggregated, social-network-federated post list for the active company.
 *
 * The expensive `getPosts` call (one request per connected network) is wrapped
 * in a single React Query so revisiting routes that consume `PostContext`
 * (Posts, Comments, sidebar badge) returns from cache instead of re-fetching
 * everything. `getPosts` itself is captured in a ref so its non-stable
 * reference doesn't churn the query key.
 */

const PostContext = React.createContext({
    posts: [],
    isLoadingPosts: false,
    isLoadingUnreadCounts: false,
    updatePosts: () => {},
})

const POSTS_STALE_TIME = 2 * 60 * 1000

const aggregatedPostsKey = (companyId, firstSocialId) => [
    'posts',
    'aggregated',
    String(companyId ?? ''),
    String(firstSocialId ?? ''),
]
const unreadCountsKey = (companyId, postIds) => [
    'comments',
    'unreadCountsByPost',
    String(companyId ?? ''),
    postIds.join(','),
]

async function fetchUnreadCountsByPost(postIds, signal) {
    if (!postIds.length) return new Map()
    const response = await api.get(
        '/comments',
        {
            countUnreadByPost: 'true',
            'filter[external_post_id]': postIds.join(','),
        },
        { timeout: 60000, signal }
    )
    const counts = response?.data ?? []
    return new Map(
        counts.map((c) => [String(c.external_post_id), c.comments_count])
    )
}

const PostProvider = ({ children }) => {
    const { company, companySocials } = useContext(CompanyContext)
    const { getPosts } = useSocialNetworks()
    const qc = useQueryClient()

    const getPostsRef = useRef(getPosts)
    getPostsRef.current = getPosts

    const companyId = company?.id
    const firstSocialId = companySocials?.[0]?.id
    const socialsBelongToCompany =
        company &&
        companySocials?.length &&
        company?.id == companySocials?.[0]?.company_id

    const postsQuery = useQuery({
        queryKey: aggregatedPostsKey(companyId, firstSocialId),
        enabled: !!socialsBelongToCompany,
        staleTime: POSTS_STALE_TIME,
        queryFn: async () => {
            const fetched = await getPostsRef.current(100, null, null)
            return (fetched || []).sort(
                (a, b) =>
                    new Date(b.created_at) - new Date(a.created_at)
            )
        },
    })

    const rawPosts = postsQuery.data
    const postIds = useMemo(
        () => (rawPosts || []).map((p) => p?.id).filter(Boolean),
        [rawPosts]
    )

    const unreadQuery = useQuery({
        queryKey: unreadCountsKey(companyId, postIds),
        enabled: postIds.length > 0,
        staleTime: 30_000,
        queryFn: ({ signal }) => fetchUnreadCountsByPost(postIds, signal),
    })

    const posts = useMemo(() => {
        if (!rawPosts) return rawPosts
        const counts = unreadQuery.data
        if (!counts) return rawPosts
        return rawPosts.map((p) => ({
            ...p,
            unreadComments_count: counts.get(String(p.id)) ?? 0,
        }))
    }, [rawPosts, unreadQuery.data])

    const updatePosts = useCallback(() => {
        qc.invalidateQueries({
            queryKey: aggregatedPostsKey(companyId, firstSocialId),
        })
        qc.invalidateQueries({
            queryKey: ['comments', 'unreadCountsByPost'],
        })
    }, [qc, companyId, firstSocialId])

    useBus(
        BULK_PROGRESS_READY_EVENT,
        () => {
            updatePosts()
        },
        [updatePosts]
    )

    const value = useMemo(
        () => ({
            posts: socialsBelongToCompany ? posts : [],
            isLoadingPosts: !!socialsBelongToCompany && postsQuery.isLoading,
            isLoadingUnreadCounts: unreadQuery.isLoading,
            updatePosts,
        }),
        [
            posts,
            postsQuery.isLoading,
            unreadQuery.isLoading,
            updatePosts,
            socialsBelongToCompany,
        ]
    )

    return (
        <PostContext.Provider value={value}>{children}</PostContext.Provider>
    )
}

export { PostContext, PostProvider }
